// src/shared/open-file.js — open a stored file the browser will actually open.
//
// ONE opener, because this has now been got wrong three separate times.
//
// THE TRAP. A `data:` URL on a download anchor is a SILENT NO-OP in several real
// places: iOS PWAs ignore `download` in standalone mode, and Chrome/Edge quietly
// drop large data: downloads. Nothing throws. The user taps, nothing happens, and
// there is no error to report — which is exactly how it gets reported as "the file
// is not showing".
//
// History, so nobody re-derives it a fourth time:
//   * v125.81 — the STAFF task opener was fixed (Matt: "in tasks, they can see a
//     preview, but can not open them") by converting to a Blob + window.open.
//   * v129.112 — Head Office, 13 Aug: "task - pdf file is not showing in the
//     attachement". The ADMIN app still had the original data:-anchor, and the
//     v129.109 task-evidence viewer had inherited it too. The fix existed for two
//     months in a file three directories away.
//
// So: Blob + object URL, window.open first (iOS opens PDFs in its viewer,
// desktop opens a tab), download anchor as the popup-blocked fallback, and the
// raw data: anchor only as a last resort.

// Pure: data URL → { bytes, mime }, or null when it is not a data URL we can read.
// Exported so the decode is testable without a DOM.
//
// v129.122 (Matt, 13 Aug: 🎤 playback said "unavailable"): the media type may
// carry PARAMETERS — `data:audio/webm;codecs=opus;base64,…` is exactly what
// Chrome's MediaRecorder produces, and it is a perfectly legal data URL. The old
// pattern read the type as `[^;,]+`, so the first `;` ended it and the trailing
// `;base64` no longer lined up: the whole match failed and every Chrome-recorded
// voice note decoded to null. iOS (plain `audio/mp4`) worked, which is how it got
// past me. The type is now everything up to the last `;base64` before the comma,
// parameters included — the Worker's VOICE_MIME already accepted them, so this
// was the client half disagreeing with the server half.
export function decodeDataUrl(dataUrl, contentType) {
  const m = /^data:([^,]*?)(;base64)?,([\s\S]*)$/.exec(String(dataUrl || ''));
  if (!m) return null;
  const mime = contentType || m[1] || 'application/octet-stream';
  try {
    let bytes;
    if (m[2]) {
      const raw = atob(m[3]);
      bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(m[3]));
    }
    return { bytes, mime };
  } catch (e) { return null; }
}

export function isImageType(contentType) {
  return String(contentType || '').indexOf('image/') === 0;
}

// Full-screen image viewer — tap anywhere to dismiss. Images are shown inline
// rather than downloaded because that is what people expect of a photo.
function showImage(dataUrl) {
  const v = document.createElement('div');
  v.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.9);z-index:99999;display:flex;'
    + 'align-items:center;justify-content:center;cursor:zoom-out';
  const img = document.createElement('img');
  img.src = dataUrl;                       // set as a property — never string-built into innerHTML
  img.alt = '';
  img.style.cssText = 'max-width:96vw;max-height:92vh;border-radius:6px';
  v.appendChild(img);
  v.addEventListener('click', () => v.remove());
  document.body.appendChild(v);
}

// Open ANY stored file. Returns 'image' | 'blob' | 'download' | 'fallback' so a
// caller (and a test) can tell which path was taken.
export function openStoredFile(dataUrl, filename, contentType) {
  if (!dataUrl) return null;
  if (isImageType(contentType)) { showImage(dataUrl); return 'image'; }
  const decoded = decodeDataUrl(dataUrl, contentType);
  if (decoded) {
    try {
      const url = URL.createObjectURL(new Blob([decoded.bytes], { type: decoded.mime }));
      const w = window.open(url, '_blank');
      let how = 'blob';
      if (!w) {                              // popup blocked → download the object URL
        const a = document.createElement('a');
        a.href = url; a.download = filename || 'attachment'; a.target = '_blank';
        document.body.appendChild(a); a.click(); a.remove();
        how = 'download';
      }
      // Revoked late: a tab that is still loading the blob must not have it
      // pulled out from under it.
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* already gone */ } }, 60000);
      return how;
    } catch (e) { /* fall through to the last resort */ }
  }
  // Last resort — the very path that fails silently, kept only for the case where
  // Blob/URL are unavailable. Better a small chance than none.
  const a2 = document.createElement('a');
  a2.href = dataUrl; a2.download = filename || 'attachment';
  document.body.appendChild(a2); a2.click(); a2.remove();
  return 'fallback';
}
